// src/App.js
import React, { useState, useRef, useEffect } from 'react';
import Docs from './pages/Docs';
import './App.css';

function App() {
  const [inputValue, setInputValue] = useState('');
  const [isValid, setIsValid] = useState(true);
  const [showError, setShowError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [mode, setMode] = useState('home'); // 'home', 'select', 或 'docs'
  const [selectedWord, setSelectedWord] = useState(null);
  const [docsLanguage, setDocsLanguage] = useState('en'); // Docs页面语言
  const inputRef = useRef(null);

  // 检查输入是否为英文文本
  const isValidEnglishText = (text) => {
    const englishRegex = /^[A-Za-z0-9\s.,!?'"()\-:;]+$/;
    return englishRegex.test(text) || text === '';
  };

  // 处理输入变化
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    
    if (!isValidEnglishText(value)) {
      setIsValid(false);
    } else {
      setIsValid(true);
    }
  };

  // 处理提交 - 进入选词模式
  const handleSubmit = () => {
    if (!inputValue.trim()) {
      return;
    }

    if (!isValid) {
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
      }, 3000);
      return;
    }

    setIsLoading(true);
    
    // 模拟API调用延迟
    setTimeout(() => {
      setIsLoading(false);
      setMode('select'); // 切换到选词模式
    }, 1000);
  };

  // 处理单词选择
  const handleWordSelect = (word) => {
    setSelectedWord(word);
    console.log('Selected word:', word);
  };

  // 处理单词分析
  const handleAnalyzeWord = () => {
    if (selectedWord) {
      console.log('Analyzing word:', selectedWord);
      alert(`Analyzing word: "${selectedWord}"...`);
    }
  };

  // 返回首页
  const handleBackToHome = () => {
    setMode('home');
    setSelectedWord(null);
    setInputValue('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // 跳转到文档页面
  const handleShowDocs = () => {
    setMode('docs');
  };

  // 处理键盘事件
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  // 页面加载时自动聚焦输入框
  useEffect(() => {
    if (inputRef.current && mode === 'home') {
      inputRef.current.focus();
    }
  }, [mode]);

  // 箭头按钮的SVG图标
  const ArrowIcon = () => (
    <svg viewBox="0 0 24 24">
      <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
    </svg>
  );

  // 返回图标
  const BackIcon = () => (
    <svg viewBox="0 0 24 24">
      <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
    </svg>
  );

  // 拆分句子为单词和标点符号
  const splitSentence = (sentence) => {
    const segments = [];
    const regex = /([A-Za-z']+)|([\s.,!?'"()\-:;])/g;
    let match;
    
    while ((match = regex.exec(sentence)) !== null) {
      if (match[1]) {
        // 单词
        segments.push({ type: 'word', content: match[1] });
      } else if (match[2]) {
        // 标点符号或空格
        segments.push({ type: match[2].trim() ? 'punctuation' : 'space', content: match[2] });
      }
    }
    
    return segments;
  };

  // Docs模式
  if (mode === 'docs') {
    return (
      <div className="app">
        <nav className="navbar">
          <button className="back-button" onClick={handleBackToHome}>
            <BackIcon />
            Home
          </button>
        </nav>
        <Docs 
          language={docsLanguage}
          onLanguageChange={() => setDocsLanguage(docsLanguage === 'en' ? 'zh' : 'en')}
        />
      </div>
    );
  }

  // 首页模式
  if (mode === 'home') {
    return (
      // ① 添加 home-layout 类，用于启用 flex 列布局
      <div className="app home-layout">
        <nav className="navbar">
          <button className="docs-link" onClick={handleShowDocs}>
            Docs
          </button>
        </nav>

        {/* ② 将除 footer 外的所有内容包裹在 home-content 中，使其弹性伸缩 */}
        <div className="home-content">
          <div className="logo-container">
            <img 
              src="/arky-logo.png" 
              alt="ContextDictionary Logo" 
              className="logo-image"
            />
          </div>

          {showError && (
            <div className="error-message">
              Please enter English text only.
            </div>
          )}

          <div className="search-container">
            <input
              ref={inputRef}
              type="text"
              className="search-input"
              placeholder="English text only"
              value={inputValue}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
            />
            <button
              className={`search-button ${inputValue.trim() && isValid ? 'active' : ''}`}
              onClick={handleSubmit}
              disabled={!inputValue.trim() || !isValid || isLoading}
            >
              {isLoading ? (
                <div className="loading"></div>
              ) : (
                <ArrowIcon />
              )}
            </button>
          </div>

          <div className="instructions-minimal">
            <p className="instruction-item">Please enter one or more English sentences. For best results, please check your spelling.</p>
            <div className="example-minimal">
              <p className="example-minimal-text">
                e.g. To read well, that is, to read true books in a true spirit, is a noble exercise, and one that will task the reader more than any exercise which the customs of the day esteem.
              </p>
            </div>
          </div>
        </div> {/* 结束 home-content */}

        {/* Footer 保持原样，自然跟在 home-content 之后 */}
        <footer className="home-site-footer">
          <div className="home-footer-content">
            <span>© 2026 Arky Context Dictionary. All rights reserved.</span>
            <span>
              <a
                href="https://beian.miit.gov.cn/"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="beian-link"
              >
                粤ICP备2026001874号-1
              </a>
            </span>
            <span>
              <img src="/beian.png" alt="公安备案图标" className="beian-icon" />
              <a
                href="https://beian.mps.gov.cn/#/query/webSearch?code=44030002010758"
                target="_blank"
                rel="noreferrer"
                className="beian-link"
              >
                粤公网安备44030002010758号
              </a>
            </span>
          </div>
        </footer>
      </div>
    );
  }

  // 选词模式
  const sentenceSegments = splitSentence(inputValue);

  return (
    <div className="app select-layout">
      <nav className="navbar">
        <button className="back-button" onClick={handleBackToHome}>
          <BackIcon />
          Home
        </button>
        <button className="docs-link" onClick={handleShowDocs}>
          Docs
        </button>
      </nav>

      <div className="select-content">
        <div className="logo-container">
          <img 
            src="/arky-logo.png" 
            alt="ContextDictionary Logo" 
            className="logo-image"
          />
        </div>

        {/* 句子容器 - 可点击的单词 */}
        <div className="sentence-container">
          <div className="sentence-text">
            {sentenceSegments.map((segment, index) => {
              if (segment.type === 'word') {
                return (
                  <span
                    key={index}
                    className={`word-span ${selectedWord === segment.content ? 'selected' : ''}`}
                    onClick={() => handleWordSelect(segment.content)}
                  >
                    {segment.content}
                  </span>
                );
              } else if (segment.type === 'punctuation') {
                return (
                  <span key={index} className="punctuation">
                    {segment.content}
                  </span>
                );
              } else {
                // 空格
                return (
                  <span key={index} className="space">
                    {segment.content}
                  </span>
                );
              }
            })}
          </div>
        </div>

        {/* 选择结果 - 只有当有选中单词时才显示 */}
        {selectedWord && (
          <div className="selection-result-container">
            <div className="selected-word-display">
              <span className="selected-label">Selected word: </span>
              <span className="selected-word">{selectedWord}</span>
            </div>
            <div className="action-buttons">
              <button className="analyze-button" onClick={handleAnalyzeWord}>
                Analyze
              </button>
            </div>
          </div>
        )}

        {/* 使用说明 */}
        <div className="tips-minimal">
          <p className="tips-item">Click a word to analyze.</p>
        </div>
      </div>
    </div>
  );
}

export default App;